/**
 * HTTP client of the can-do path (plan 46, `docs/speak-plan/46-can-do-path.md`
 * § API): `/api/modules/english/can-do/*`. The types mirror the DTOs of
 * `packages/modules/languages/src/can-do/view.ts` (UI-facing copies, same
 * criterion as `programApi.ts`: speak stays self-contained).
 *
 * **The flag.** Every route answers `404 { error: 'can_do_path_off' }` while
 * `module_config.languages.canDoPath` is off. That exact code is the ONLY
 * signal the app reads as "flag off" (`isCanDoPathOff`); any other failure is
 * an error and surfaces as one — never as a silent fallback to the old
 * Program. Each answer also updates the session's flag knowledge
 * (`knownCanDoFlag`), so a surface that only needs to know whether the path is
 * on (the radar) does not have to probe again.
 *
 * Envelope: strict — the routes always answer `{ success: true, data }`, and a
 * body without `data` (or with the wrong shape) throws `bad_can_do_payload`.
 */
import { createTypedApiClient } from '@orborix/auth-client';
import { createSessionCache } from './sessionCache.js';
import type { SubUnitTheory } from './programApi.js';

const BASE = '/api/modules/english';

/** The error code every can-do route answers while the flag is off. */
export const CAN_DO_PATH_OFF = 'can_do_path_off';

export type CanDoLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

/** Learner copy: every string of the path comes in both languages. */
export interface LocalizedText {
  es: string;
  en: string;
}

export type CanDoSkillState = 'unseen' | 'practised' | 'used' | 'solid';
export type CanDoSkillArea = 'grammar' | 'words' | 'conversation';

export interface CanDoSkillRef {
  skillId: string;
  label: LocalizedText;
  area: CanDoSkillArea;
  level: string;
  state: CanDoSkillState;
}

export type CanDoStep = { kind: 'lesson'; lessonId: string } | { kind: 'capstone' };

/** The `moduleHints` the existing WS start sends to run a step (see `canDoStart.ts`). */
export type CanDoStartHints =
  | { mode: 'plan'; overrideUnitId: string; overrideSubUnitId: string }
  | { mode: 'plan'; overrideUnitId: string; scenarioId: string };

export type CanDoUnitStatus = 'not_started' | 'in_progress' | 'completed' | 'mastered';
export type CanDoLessonStatus = 'available' | 'in_progress' | 'completed';
export type CanDoCapstoneStatus = 'not_started' | 'attempted' | 'passed';

export interface CanDoLessonSummary {
  id: string;
  order: number;
  title: LocalizedText;
  status: CanDoLessonStatus;
  objectivesMet: number;
  objectivesTotal: number;
}

export interface CanDoUnitSummary {
  id: string;
  order: number;
  family: string;
  served: boolean;
  canDo: LocalizedText;
  /** `coming_soon` = a draft unit: listed, never startable. */
  status: CanDoUnitStatus | 'coming_soon';
  /** Below the learner's level and untouched: "probablemente ya lo sabés · repasar". */
  assumedKnown: boolean;
  lessons: CanDoLessonSummary[];
  lessonsDone: number;
  lessonsTotal: number;
  capstone: { title: LocalizedText; status: CanDoCapstoneStatus; attempts: number };
  /** The unit's next step; `null` once completed, or when not served. */
  next: { step: CanDoStep; start: CanDoStartHints } | null;
}

export interface CanDoLevelView {
  level: CanDoLevel;
  /** "N de 10 logrados" = unitsCompleted / unitsTotal. */
  unitsTotal: number;
  unitsServed: number;
  unitsCompleted: number;
  /** "Usás X de Y cosas que un B1 hace". */
  skillsUsed: number;
  skillsTotal: number;
  units: CanDoUnitSummary[];
}

export type CanDoNextReason = 'continue_unit' | 'radar_focus' | 'next_in_order';

export type CanDoNextStepView =
  | {
      kind: 'step';
      unitId: string;
      step: CanDoStep;
      reason: CanDoNextReason;
      focusSkillId?: string;
      canDo: LocalizedText;
      title: LocalizedText;
      start: CanDoStartHints;
      /** `radar_focus` on a lesson that practises the skill: its label, what the CTA names. */
      focusSkillLabel?: LocalizedText;
    }
  | { kind: 'level_complete'; level: CanDoLevel }
  | { kind: 'no_units'; level: CanDoLevel };

/** A track beside the levels (plan 48, phase 4): `first-steps` is the A0 learner's entry path. */
export type CanDoTrack = 'first-steps';

export interface CanDoTrackView {
  track: CanDoTrack;
  /** "N de 4 logrados". */
  unitsTotal: number;
  unitsServed: number;
  unitsCompleted: number;
  units: CanDoUnitSummary[];
}

export interface CanDoPathView {
  learnerLevel: CanDoLevel;
  servedLevels: CanDoLevel[];
  levels: CanDoLevelView[];
  /** The first-steps track: present only for an A0 learner (shown before the levels). */
  firstSteps: CanDoTrackView | null;
  next: CanDoNextStepView;
}

export interface CanDoReadingRef {
  ref: string;
  title: LocalizedText;
}

export interface CanDoLessonDetail {
  id: string;
  order: number;
  title: LocalizedText;
  blurb: LocalizedText;
  status: CanDoLessonStatus;
  focus: CanDoSkillRef;
  objectives: Array<CanDoSkillRef & { reps: number }>;
  reading: CanDoReadingRef;
  /** "Repasá también": the reading of every other skill the lesson asks for. */
  review: Array<{
    skill: CanDoSkillRef;
    reading: CanDoReadingRef;
    from: { kind: 'lesson'; lessonId: string } | { kind: 'library' };
  }>;
  /** "Ya lo usás · opcional": every skill of the lesson is already ≥ used. */
  alreadyUsed: boolean;
  start: CanDoStartHints;
}

export interface CanDoMissionObjective {
  id: string;
  label: LocalizedText;
  kind: 'primary' | 'bonus';
}

export interface CanDoCapstoneDetail {
  scenarioId: string;
  title: LocalizedText;
  briefing: LocalizedText;
  emoji: string;
  durationMin: number;
  mission: CanDoMissionObjective[];
  watched: CanDoSkillRef[];
  /** All lessons completed: the suggested moment (never a lock). */
  ready: boolean;
  status: CanDoCapstoneStatus;
  attempts: number;
  bestPrimaryMet: number;
  bestPrimaryTotal: number;
  start: CanDoStartHints;
}

export interface CanDoUnitDetail extends CanDoUnitSummary {
  why: LocalizedText;
  lessonsDetail: CanDoLessonDetail[];
  capstoneDetail: CanDoCapstoneDetail;
  grammar: Array<CanDoSkillRef & { hasReading: boolean }>;
  coreSkills: CanDoSkillRef[];
  wordSet: { tag: string; lemmas: string[]; used: string[] };
}

export interface CanDoReading extends CanDoReadingRef {
  theory: SubUnitTheory;
}

export interface CanDoLibraryEntry {
  skillId: string;
  label: LocalizedText;
  level: string;
  readings: CanDoReadingRef[];
  /** Served lessons that practise the skill (focus lessons first). */
  practisedIn: Array<{ unitId: string; lessonId: string; focus: boolean }>;
}

export interface CanDoLibraryGroup {
  id: string;
  title: LocalizedText;
  entries: CanDoLibraryEntry[];
}

export interface CanDoLibrary {
  levels: string[];
  groups: CanDoLibraryGroup[];
}

export interface CanDoLibrarySkill {
  skillId: string;
  label: LocalizedText;
  level: string;
  readings: CanDoReading[];
}

export interface CanDoPractice {
  unitId: string;
  lessonId: string;
  focus: boolean;
  start: CanDoStartHints;
}

const client = createTypedApiClient({
  basePath: BASE,
  errorName: 'CanDoApiError',
  // Every reader passes its own `signal` (the hooks abort on unmount): an
  // abort is "I cancelled", not a network failure — same as `programApi`.
  abortError: 'rethrow',
});

export const CanDoApiError = client.ApiError;
export type CanDoApiError = InstanceType<typeof client.ApiError>;

/** `true` ONLY for the explicit flag-off answer (404 + `can_do_path_off`). */
export function isCanDoPathOff(err: unknown): boolean {
  return err instanceof CanDoApiError && err.status === 404 && err.code === CAN_DO_PATH_OFF;
}

// ─── What the session knows about the flag ──────────────────────────────────
// Set by every can-do answer (any success ⇒ on; `can_do_path_off` ⇒ off);
// cleared with the other session caches on an identity change.
const flagCache = createSessionCache<'on' | 'off'>();

/** The flag as the last can-do answer of this session showed it, or `null` if none came yet. */
export function knownCanDoFlag(): 'on' | 'off' | null {
  return flagCache.get();
}

function bad(path: string, what: string): never {
  throw new CanDoApiError(`can-do ${path}: ${what}`, 500, 'bad_can_do_payload');
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

async function data(path: string, init: RequestInit = {}): Promise<Record<string, unknown>> {
  try {
    const res = await client.request(path, init);
    await client.ensureOk(res);
    const body: unknown = await res.json();
    if (!isObject(body) || !isObject(body['data'])) bad(path, 'the answer has no `data` object');
    flagCache.set('on');
    return body['data'];
  } catch (err) {
    if (isCanDoPathOff(err)) flagCache.set('off');
    throw err;
  }
}

const opt = (signal?: AbortSignal): RequestInit => (signal ? { signal } : {});

/**
 * Validates `moduleHints` from the server: `mode: 'plan'`, the unit, and
 * exactly one of the lesson / the capstone's scenario. Anything else throws —
 * a key the app would not forward must not be dropped in silence.
 */
export function parseStartHints(raw: unknown, where = 'start'): CanDoStartHints {
  if (!isObject(raw)) bad(where, 'moduleHints is not an object');
  const keys = Object.keys(raw).sort().join(',');
  const unit = raw['overrideUnitId'];
  if (raw['mode'] !== 'plan' || typeof unit !== 'string' || unit.length === 0) {
    bad(where, `unexpected moduleHints ${JSON.stringify(raw)}`);
  }
  const sub = raw['overrideSubUnitId'];
  const scenario = raw['scenarioId'];
  if (keys === 'mode,overrideSubUnitId,overrideUnitId' && typeof sub === 'string' && sub) {
    return { mode: 'plan', overrideUnitId: unit, overrideSubUnitId: sub };
  }
  if (keys === 'mode,overrideUnitId,scenarioId' && typeof scenario === 'string' && scenario) {
    return { mode: 'plan', overrideUnitId: unit, scenarioId: scenario };
  }
  return bad(where, `unexpected moduleHints ${JSON.stringify(raw)}`);
}

function checkPath(d: Record<string, unknown>, path: string): CanDoPathView {
  if (
    typeof d['learnerLevel'] !== 'string' ||
    !Array.isArray(d['levels']) ||
    !isObject(d['next']) ||
    !('firstSteps' in d) ||
    (d['firstSteps'] !== null &&
      (!isObject(d['firstSteps']) || !Array.isArray(d['firstSteps']['units'])))
  ) {
    bad(path, 'not a CanDoPathView');
  }
  return d as unknown as CanDoPathView;
}

/** The whole path: the learner's level, every level with units, the next step. */
export async function fetchCanDoPath(signal?: AbortSignal): Promise<CanDoPathView> {
  return checkPath(await data('/can-do', opt(signal)), '/can-do');
}

/** Just the next step (home hero). */
export async function fetchCanDoNext(
  signal?: AbortSignal,
): Promise<{ learnerLevel: CanDoLevel; next: CanDoNextStepView }> {
  const d = await data('/can-do/next', opt(signal));
  if (typeof d['learnerLevel'] !== 'string' || !isObject(d['next'])) {
    bad('/can-do/next', 'no learnerLevel / next');
  }
  return d as unknown as { learnerLevel: CanDoLevel; next: CanDoNextStepView };
}

/** The unit panel (404 `unit_not_found` for drafts and unknown ids). */
export async function fetchCanDoUnit(
  unitId: string,
  signal?: AbortSignal,
): Promise<CanDoUnitDetail> {
  const path = `/can-do/units/${encodeURIComponent(unitId)}`;
  const d = await data(path, opt(signal));
  if (!Array.isArray(d['lessonsDetail']) || !isObject(d['capstoneDetail'])) {
    bad(path, 'not a CanDoUnitDetail');
  }
  return d as unknown as CanDoUnitDetail;
}

/** A lesson's reading (4 variants). */
export async function fetchCanDoLessonTheory(
  unitId: string,
  lessonId: string,
  signal?: AbortSignal,
): Promise<CanDoReading> {
  const path = `/can-do/units/${encodeURIComponent(unitId)}/lessons/${encodeURIComponent(lessonId)}/theory`;
  const d = await data(path, opt(signal));
  if (typeof d['ref'] !== 'string' || !isObject(d['theory'])) bad(path, 'not a reading');
  return d as unknown as CanDoReading;
}

/** Validates a step server-side and returns the `moduleHints` of the WS start. */
export async function startCanDoStep(
  unitId: string,
  step: { lessonId: string } | { capstone: true },
): Promise<CanDoStartHints> {
  const path = `/can-do/units/${encodeURIComponent(unitId)}/start`;
  const d = await data(path, { method: 'POST', body: JSON.stringify(step) });
  return parseStartHints(d['moduleHints'], path);
}

/** The grammar library, grouped by family (no `level` = the learner's band). */
export async function fetchCanDoLibrary(
  level?: string,
  signal?: AbortSignal,
): Promise<CanDoLibrary> {
  const path = `/can-do/library${level ? `?level=${encodeURIComponent(level)}` : ''}`;
  const d = await data(path, opt(signal));
  if (!Array.isArray(d['levels']) || !Array.isArray(d['groups'])) bad(path, 'not a library');
  return d as unknown as CanDoLibrary;
}

/** One library skill with its readings' texts (404 `skill_not_found` without readings). */
export async function fetchCanDoLibrarySkill(
  skillId: string,
  signal?: AbortSignal,
): Promise<CanDoLibrarySkill> {
  const path = `/can-do/library/${encodeURIComponent(skillId)}`;
  const d = await data(path, opt(signal));
  if (!Array.isArray(d['readings'])) bad(path, 'no readings');
  return d as unknown as CanDoLibrarySkill;
}

/**
 * Radar → lesson: the lesson that targets a skill. `null` for the explicit
 * "no lesson targets it" answer (404 `no_lesson_for_skill`); anything else
 * that fails throws.
 */
export async function fetchCanDoPractice(
  skillId: string,
  signal?: AbortSignal,
): Promise<CanDoPractice | null> {
  const path = `/can-do/practice/${encodeURIComponent(skillId)}`;
  try {
    const d = await data(path, opt(signal));
    if (typeof d['unitId'] !== 'string' || typeof d['lessonId'] !== 'string') {
      bad(path, 'not a practice target');
    }
    return { ...(d as unknown as CanDoPractice), start: parseStartHints(d['start'], path) };
  } catch (err) {
    if (err instanceof CanDoApiError && err.status === 404 && err.code === 'no_lesson_for_skill') {
      return null;
    }
    throw err;
  }
}
